<template lang="pug">
.vue-time-entries-header
  .page-header-wrapper.row
    .col-lg-4.col-sm-4
      h2.page-heading Screenshots
    .col-lg-4.col-sm-6.text-center
      ActivitiesAllScreenshotsToggle(
        :toggled="allView"
        @change="updateTab"
      )
    .col-lg-4.col-sm-2
      .settings-link-wrapper.pull-right(
        v-if="permittedToManageSettings"
        data-testid="activities-settings-link"
      )
        a.settings-link.pull-left(
          v-if="organizationId"
          :href="`/organizations/${organizationId}/settings/screenshots`"
          target="_blank"
        )
          i.hi.hi-settings
          |
          | Settings
  .alert(v-if="fakeData.required && !fakeData.show")
    i.fa.fa-clock-o.mr-5
    span(v-if="fakeData.team")
      | Your team has not tracked any time.
    span(v-else)
      | You have not tracked any time.
    a.clickable.ml-5(@click="showOverlay") Get Started
  .row.full-width-selects-sm.basic-filters.mt-20
    .col-sm-12.col-lg-6.weekly-range-input-wrapper
      .weekly-range-input.mr-10.inline-block
        AppDatePicker.pr-0(
          v-model="date"
          v-tooltip
          hide-today
          disable-future
          :title="isMobile ? '' : `Use ‘Ctrl/Cmd + left/right’ to quickly switch between dates`"
          :min-date="minDate"
          :time-data-dates="timeDataDates"
          :max-date="maxDate"
          :start-week-on="startWeekOn"
          type="daily"
          :current-zone="currentZone"
        )
      .valign-top.inline-block
        AppTimeZoneDropdown(
          :active="currentZone"
          :options="timeZones"
          direction="right"
          @input="updateTimeZone"
        )
    .col-sm-12.col-lg-6.selections
      .d-flex.gap-15.align-items-center.justify-content-end
        ActivitiesLazyMembersSelect.activities-members

        AppButton.filters-button(
          data-testid="activities-filters-button"
          modifiers="btn-outline-primary"
          type=""
          @click.stop="toggleFilters"
        ) Filters
  .row
    .col-sm-4.total-container.hidden-xs
    .col-sm-4.p-0.text-center(v-if="filterCount > 0")
      AppFilterCount(
        :count="filterCount"
        @click="toggleFilters"
        @clear="clearFilters"
      )
  ActivitiesInfoBanner(v-if="showInfoBanner")
  .row
    .col-sm-4.page-header-actions.pull-right
      ActivitiesNotesButton(
        :user-id="queryUserId"
        :organization-id="organizationId"
        :date="queryDate"
        :project-id="queryProjectId"
        :current-zone="currentZone"
      )
      ActivitiesDownloadButton(
        v-if="permittedToManageSettings"
        :user-id="queryUserId"
        :organization-id="organizationId"
      )
  ActivitiesFilterSidebar(
    :visible="showFilters"
    @close="closeFilters"
  )
  template(v-if="blockView")
    ActivitiesBenchmarks(:showing-overlay="showingOverlay")
</template>

<script>
import { mapGetters, mapState, mapActions } from 'vuex';
import { createWithZone, formatInternalDate } from '~/utils/time_utils';
import { isMobile } from '~/mixins';

import ActivitiesBenchmarks from './benchmarks/ActivitiesBenchmarks.vue';
import ActivitiesDownloadButton from './ActivitiesDownloadButton.vue';
import ActivitiesFilterSidebar from './ActivitiesFilterSidebar.vue';
import ActivitiesNotesButton from './ActivitiesNotesButton.vue';
import ActivitiesLazyMembersSelect from './ActivitiesLazyMembersSelect.vue';
import ActivitiesInfoBanner from './ActivitiesInfoBanner.vue';
import ActivitiesAllScreenshotsToggle from './ActivitiesAllScreenshotsToggle.vue';

export default {
  name: 'ActivitiesHeader',
  components: {
    ActivitiesBenchmarks,
    ActivitiesDownloadButton,
    ActivitiesFilterSidebar,
    ActivitiesLazyMembersSelect,
    ActivitiesNotesButton,
    ActivitiesInfoBanner,
    ActivitiesAllScreenshotsToggle,
  },
  mixins: [isMobile],
  props: {
    showingOverlay: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    showFilters: false,
  }),
  computed: {
    ...mapState({
      activitiesQuery: (state) => state.activities_query,
      fakeData: (state) => state.fake_data,
      permittedToManageSettings: (state) => state.current_user.can_manage_settings,
      startWeekOn: (state) => state.organization.start_week_on,
      timeDataDates: (state) => state.time_data_dates,
    }),
    ...mapGetters([
      'allView',
      'allPresentActivities',
      'blockView',
      'currentZone',
      'organizationId',
      'timeZones',
      'queryUserId',
      'queryDate',
      'queryProjectId',
      'queryFilters',
      'hideActivityInfoBanner',
      'currentZone',
    ]),
    date: {
      get() {
        return createWithZone(this.activitiesQuery.date, null, this.currentZone.name);
      },
      set(value) {
        window.analytics?.track('Activities date apply', {
          new_date: formatInternalDate(value),
          previous_date: formatInternalDate(value),
        });

        this.$store.dispatch('setFilter', { date: formatInternalDate(value) });
      },
    },
    minDate() {
      const date = createWithZone(this.activitiesQuery.min_date, null, this.currentZone.name);

      return formatInternalDate(date);
    },
    maxDate() {
      const date = moment.tz(this.currentZone.name);

      return formatInternalDate(date);
    },
    filterCount() {
      if (!this.queryFilters) {
        return 0;
      }

      let filterCount = 0;
      const activityLevel = this.queryFilters.activity_level;
      const clientTypes = this.queryFilters.client_types;
      const project = this.queryFilters.project;
      const timeTypes = this.queryFilters.time_types;

      if (activityLevel) {
        filterCount += 1;
      }

      if (clientTypes) {
        filterCount += clientTypes.length;
      }

      if (project) {
        filterCount += 1;
      }

      if (timeTypes) {
        filterCount += timeTypes.length;
      }

      return filterCount;
    },
    showInfoBanner() {
      const hasNonDesktopActivity = this.allPresentActivities.some(
        (activity) => activity.web_time || activity.manual_time || activity.mobile_time,
      );

      return !this.hideActivityInfoBanner && hasNonDesktopActivity;
    },
  },
  methods: {
    ...mapActions(['clearFilters']),
    updateTab(allScreenshots) {
      this.$emit('change', allScreenshots);
    },
    updateTimeZone(entity) {
      const activeZone = this.timeZones.find((tz) => tz.for === entity)?.name;
      let date = moment.tz(this.date, this.currentZone.name).add(12, 'hours').tz(activeZone).format('YYYY-MM-DD');
      const min_date = moment
        .tz(this.minDate, this.currentZone.name)
        .add(12, 'hours')
        .tz(activeZone)
        .format('YYYY-MM-DD');

      if (date > this.maxDate) {
        date = this.maxDate;
      }

      // Pass the slot entity (e.g. "org-1"); the widget query resolves it to the IANA name via currentZone.
      this.$store.dispatch('setFilter', { date, min_date, time_zone: entity });
    },
    toggleFilters(event) {
      this.showFilters = !this.showFilters;

      event.target.parentElement.dispatchEvent(new Event('click', { bubbles: true }));
    },
    closeFilters() {
      this.showFilters = false;
    },
    showOverlay() {
      this.$emit('show-overlay');
    },
  },
};
</script>
